import { useMemo, useCallback, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters } from "@/services/api/types";
import { getEnumTKey } from "@/utils/strings";
import { tWithFallback } from "@/utils/i18n";
import { isArray, uniqBy, startCase } from "lodash";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";
import {
  EntityType,
  getEntityHook
} from "@/services/hooks/entities/useEntityType";
import { NumericCondition } from "@/pages/deals/numericCondition";
import { NumericFilterValue } from "@/components/controls/ToolbarActions/ToolbarFilterButton/types";
import { getSingleSelectOption } from "@/services/hooks/entities/useEntitySelect";
import { ColumnData } from "@/entities/dataTables/types";
import { APIFieldItem } from "@/pages/settings/types";
import { mapCustomFieldsToFilterOptions } from "@/entities/dataTables/helpers";
import { TextVariant } from "@/components/ui/text";
import { isNotInFilter } from "@/services/api/noValueFilters";

export { isNotInFilter };

const FILTER_TRANSLATION_KEY = "datatoolbar.filters";

export const extractFilterValues = (value: any): any[] => {
  if (!value) return [];
  if (isNotInFilter(value)) {
    const notInValue = value.not_in;
    return isArray(notInValue) ? notInValue : [notInValue];
  }
  return isArray(value) ? value : [value];
};

export const wrapWithNotIn = (values: any[]): { not_in: any[] } => {
  return { not_in: values };
};

const allConditionOptions = Object.values(
  NumericCondition
) as NumericCondition[];

export const getNumericCondition = (
  currentValue: NumericFilterValue | undefined | null
): NumericCondition | null => {
  if (!currentValue) return null;
  if (currentValue.min !== undefined || currentValue.max !== undefined) {
    return NumericCondition.BETWEEN;
  }
  const simpleConditions = allConditionOptions.filter(
    (opt) => opt !== NumericCondition.BETWEEN
  );
  return (
    simpleConditions.find(
      (condition) => currentValue[condition] !== undefined
    ) ?? null
  );
};

export const getNumericAmount = (
  currentValue: NumericFilterValue | undefined | null
): number | null => {
  if (!currentValue) return null;
  const condition = getNumericCondition(currentValue);
  if (!condition || condition === NumericCondition.BETWEEN) return null;
  return currentValue[condition] ?? null;
};

export const getNumericMinAmount = (
  currentValue: NumericFilterValue | undefined | null
): number | null => currentValue?.min ?? null;

export const getNumericMaxAmount = (
  currentValue: NumericFilterValue | undefined | null
): number | null => currentValue?.max ?? null;

export interface FilterOption {
  value: any;
  label: string;
  icon?: ReactElement;
  labelVariant?: TextVariant;
  isNoValue?: boolean;
}

export const NO_VALUE: null = null;

export const isNoValue = (value: any): boolean => value === null;

interface UseFilterOptionsParams {
  filterOption: DataTableFilter;
  filters: Filters;
  searchValue?: string;
  alwaysEnabled?: boolean;
}

interface UseFilterOptionsResult {
  staticOptions: FilterOption[];
  combinedOptions: FilterOption[];
  selectedValues: any[];
  useServerSearch: boolean;
  isLoading: boolean;
  onSearch: ((value: string) => void) | undefined;
  showAvatar: boolean;
  shouldShowSearch: boolean;
}

export const useFilterOptions = ({
  filterOption,
  filters,
  searchValue = "",
  alwaysEnabled = false
}: UseFilterOptionsParams): UseFilterOptionsResult => {
  const { t } = useTranslation();
  const filterKey = filterOption.key;
  const filterType = filterOption.filterType;

  const entityType = filterType.type === "map" ? filterType.entity : undefined;
  const useServerSearch = !!entityType;

  const currentValue = filters[filterKey as keyof Filters];

  const selectedValues = useMemo(() => {
    return extractFilterValues(currentValue);
  }, [currentValue]);

  const selectedId = useMemo(() => {
    if (!selectedValues || selectedValues.length === 0) return "";
    return String(selectedValues[0]);
  }, [selectedValues]);

  const serverSearch = useServerSearch
    ? useEntitySelect({
        entityType: entityType as EntityType,
        selectedId,
        alwaysEnabled
      })
    : { options: [], onSearch: undefined, isLoading: false };

  const { options: serverOptions, onSearch, isLoading } = serverSearch;

  const selectedEntityIds = useMemo(() => {
    if (!useServerSearch || selectedValues.length === 0) return [];
    return selectedValues.map((v) => String(v));
  }, [useServerSearch, selectedValues]);

  const useEntityQuery = getEntityHook(entityType);
  const selectedEntitiesQuery = useEntityQuery
    ? useEntityQuery(selectedEntityIds)
    : { entities: [], isSuccess: false };

  const { entities: selectedEntities = [] } = selectedEntitiesQuery;

  const selectedEntitiesMap = useMemo(() => {
    const map = new Map<string, string>();
    if (selectedEntities && entityType) {
      selectedEntities.forEach((entity: any) => {
        if (entity) {
          const option = getSingleSelectOption(entity, entityType);
          map.set(String(entity.id), option.displayName);
        }
      });
    }
    return map;
  }, [selectedEntities, entityType]);

  const noValueOption: FilterOption = useMemo(
    () => ({
      value: NO_VALUE,
      label: t(`${FILTER_TRANSLATION_KEY}.noValue`),
      isNoValue: true
    }),
    [t]
  );

  const includeNoValue = !filterOption.excludeNoValue;

  const staticOptions = useMemo((): FilterOption[] => {
    const prefix: FilterOption[] = includeNoValue ? [noValueOption] : [];
    if (filterType.type === "map") {
      return [
        ...prefix,
        ...filterType.values.map((item) => ({
          value: item.value,
          label: item.label
        }))
      ];
    }
    if (filterType.type === "enum") {
      const enumType = filterType.values.enumType;
      const entityType = filterType.values.entityType;
      const valueType = filterType.values.valueType;
      const getIcon = filterType.values.getIcon;
      const getLabelVariant = filterType.values.getLabelVariant;
      const excludeValues: unknown[] = filterType.values.excludeValues ?? [];

      return [
        ...prefix,
        ...Object.keys(enumType)
          .filter((key) => isNaN(Number(key)))
          .filter((key) => !excludeValues.includes(enumType[key]))
          .map((enumKey) => {
            const enumValue = enumType[enumKey];
            return {
              value: enumValue,
              label: t(getEnumTKey(entityType, valueType, enumType, enumValue)),
              icon: getIcon ? getIcon(enumValue) : undefined,
              labelVariant: getLabelVariant
                ? getLabelVariant(enumValue)
                : undefined
            };
          })
      ];
    }
    if (filterType.type === "customField") {
      const { options } = filterType.values;
      return [
        ...prefix,
        ...Object.entries(options).map(([key, value]) => ({
          value: key,
          label: value
        }))
      ];
    }
    return [];
  }, [filterType, t, noValueOption, includeNoValue]);

  // Combine server options with static options for entity filters
  const combinedOptions = useMemo((): FilterOption[] => {
    if (!useServerSearch) {
      // For non-entity filters, use client-side filtering.
      if (!searchValue.trim()) return staticOptions;
      const namedOptions = staticOptions
        .filter((option) => !option.isNoValue)
        .filter((option) =>
          option.label.toLowerCase().includes(searchValue.toLowerCase())
        );
      return includeNoValue ? [noValueOption, ...namedOptions] : namedOptions;
    }

    // For entity filters, combine server results with selected values
    const formattedServerOptions: FilterOption[] = serverOptions.map(
      (option) => ({
        value: option.id,
        label: option.displayName || ""
      })
    );

    const selectedOptionsMap = new Map<string, FilterOption>();

    // Add options from static values that are selected
    if (selectedValues.length > 0) {
      staticOptions
        .filter((item) => selectedValues.includes(item.value))
        .forEach((item) => {
          selectedOptionsMap.set(String(item.value), {
            value: item.value,
            label: item.label,
            isNoValue: item.isNoValue
          });
        });
    }

    // Add any selected values that aren't in the map yet
    selectedValues.forEach((value) => {
      const strValue = String(value);
      if (!selectedOptionsMap.has(strValue)) {
        const staticOption = staticOptions.find(
          (opt) => String(opt.value) === strValue
        );
        const serverOption = formattedServerOptions.find(
          (opt) => String(opt.value) === strValue
        );
        const entityName = selectedEntitiesMap.get(strValue);
        selectedOptionsMap.set(strValue, {
          value: value,
          label:
            entityName ||
            serverOption?.label ||
            staticOption?.label ||
            strValue,
          isNoValue: staticOption?.isNoValue
        });
      }
    });

    const selectedOptions = Array.from(selectedOptionsMap.values());

    const nonSelectedServerOptions = formattedServerOptions.filter(
      (opt) => !selectedValues.includes(opt.value)
    );

    // Pin "(No value)" to the top for entity-backed filters too. Deduping
    // happens below so a selected no-value option isn't double-rendered.
    const merged = [
      ...(includeNoValue ? [noValueOption] : []),
      ...selectedOptions,
      ...nonSelectedServerOptions
    ];

    return uniqBy(merged, (opt) => String(opt.value));
  }, [
    useServerSearch,
    staticOptions,
    serverOptions,
    selectedValues,
    searchValue,
    selectedEntitiesMap,
    noValueOption,
    includeNoValue
  ]);

  const showAvatar =
    filterType.type === "map" && filterType.showAvatar === true;

  const shouldShowSearch =
    useServerSearch ||
    (filterType.type === "map" && filterType.showSearch) ||
    staticOptions.length > 10;

  return {
    staticOptions,
    combinedOptions,
    selectedValues,
    useServerSearch,
    isLoading,
    onSearch,
    showAvatar,
    shouldShowSearch
  };
};

export const useFilterToggleWithNotIn = (
  filterKey: string,
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
) => {
  return useCallback(
    (value: any) => {
      setFilters((prev) => {
        const current = prev[filterKey as keyof Filters];
        const isCurrentNotIn = isNotInFilter(current);
        const currentArray = extractFilterValues(current);

        const isSelected = currentArray.includes(value);
        let newValues: any[];

        if (isSelected) {
          newValues = currentArray.filter((v) => v !== value);
        } else {
          newValues = [...currentArray, value];
        }

        if (newValues.length === 0) {
          const newFilters = { ...prev };
          delete newFilters[filterKey as keyof Filters];
          return newFilters;
        }

        return {
          ...prev,
          [filterKey]: isCurrentNotIn ? wrapWithNotIn(newValues) : newValues
        };
      });
    },
    [filterKey, setFilters]
  );
};

export const getFilterKeyLabel = (
  filterKey: string,
  customFieldLabel?: string
): string =>
  tWithFallback(
    `${FILTER_TRANSLATION_KEY}.sortAndGroupByOptions.${filterKey}`,
    customFieldLabel || startCase(filterKey)
  );

export const getFilterLabel = (filterOption: DataTableFilter): string =>
  getFilterKeyLabel(filterOption.key, filterOption.customFieldLabel);

export const getAllFilterOptions = (
  filterOptions: DataTableFilter[],
  columnsState?: ColumnData[],
  customFields?: APIFieldItem[]
): DataTableFilter[] => {
  if (!columnsState || !customFields) return filterOptions;
  const customFieldFilterOptions = mapCustomFieldsToFilterOptions(
    columnsState,
    customFields
  );
  const existingKeys = new Set(filterOptions.map((opt) => opt.key));
  const uniqueCustomFieldOptions = customFieldFilterOptions.filter(
    (opt) => !existingKeys.has(opt.key)
  );

  const columnOrder = new Map(columnsState.map((col) => [col.id, col.order]));
  const combined = [...filterOptions, ...uniqueCustomFieldOptions];
  return combined.sort((a, b) => {
    const aOrder = columnOrder.get(a.columnId ?? a.key) ?? Infinity;
    const bOrder = columnOrder.get(b.columnId ?? b.key) ?? Infinity;
    return aOrder - bOrder;
  });
};
